<script setup>
import { ref, computed, watch } from 'vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import Alert from '@/components/Atoms/Alert.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconCross from '@/components/icon/IconCross.vue'
import IconPlusTiny from '@/components/icon/IconPlusTiny.vue'
import IconInfoSmall from '@/components/icon/IconInfoSmall.vue'
import LanguageSelect from './LanguageSelect.vue'
import TranslationEngine from './TranslationEngine.vue'
import {
	FALLBACK_CATALOG,
	sourceOptions,
	outputOptions,
	outputDisallowedReason,
	allowedOutputsFor,
	sourceCanTranslate,
	languageName,
	joinLanguageNames,
	validateSettings,
	isProfanitySupported,
	DELAY_RECOMMENDED,
	TRANSLATION_RULE,
	LANGUAGES_EXPLAINER,
	LANGUAGES_EXPLAINER_ANY_PAIR,
	SOURCE_EXPLAINER,
	SOURCE_EXPLAINER_ANY_PAIR,
	NON_HUB_HINT,
} from './captionLanguages'
import {
	draftFromTranslations,
	buildTranslations,
	translationsKey,
	translatedLangs,
	translationErrorsFor,
	mapServerTranslationErrors,
	requiredGeminiLangs,
	optionsFor,
} from './captionTranslations'
import {
	DEFAULT_LIMITS,
	DEFAULT_USAGE,
	UPGRADE_ROUTE,
	hasPool,
	poolExhausted,
	concurrencyBlocked,
	poolExhaustedTooltip,
	concurrencyTooltip,
	durationPresetsFor,
	formatHours,
} from './captionLimits'
import { track, EVENTS } from '@/lib/tracking'

const props = defineProps({
	// The saved flow; the form re-seeds from it whenever it changes.
	flow: { type: Object, required: true },
	// Language catalog from core (GET /captions/languages) or the local fallback.
	catalog: { type: Object, default: () => FALLBACK_CATALOG },
	saving: { type: Boolean, default: false },
	// { status, message, code, title?, upgrade? } from the last failed save/start/extend, or null.
	serverError: { type: Object, default: null },
	disabled: { type: Boolean, default: false },
	// Caption window from `windowOf(status, flow)`: { state, durationSec, expiresAt, endedReason, ... }.
	// The running window's own controls live in the session card (CaptionStatusStrip).
	window: { type: Object, default: () => ({ state: 'idle', durationSec: null, expiresAt: null }) },
	// Plan limits and live usage (captionLimits.js). Defaults = no plan gates.
	limits: { type: Object, default: () => ({ ...DEFAULT_LIMITS }) },
	usage: { type: Object, default: () => ({ ...DEFAULT_USAGE }) },
	starting: { type: Boolean, default: false },
})

const emit = defineEmits(['save', 'retry', 'start'])

// Duration for the next Start: the user's chosen maximum window.
const durationSec = ref(3600)

const source = ref('')
const langs = ref([])
// Broadcast delay is not user-editable: the UI always sends the recommended
// value and core defaults to the same.
const delaySec = DELAY_RECOMMENDED
const touched = ref({ source: false, langs: false })
const attempted = ref(false)
// "Removed French: …" after a source change dropped now-invalid outputs.
const pruneNotice = ref('')
// { source, langs } before that change, for Undo.
const pruneUndo = ref(null)
// Translation engine draft: { gemini, options: { [lang]: {...} } }.
const translationDraft = ref(draftFromTranslations({}))
// Field errors from a 400 `captions_invalid_translations`, kept with the value
// they were raised on so editing the field clears them:
// { [lang]: { [field]: { message, value } } }.
const serverTranslationErrors = ref({})

// ---------------------------------------------------------------- Plan gates
// `enforced: false` is reporting mode: the numbers show, nothing is blocked.
const planActive = computed(() => props.limits?.enforced === true)
const poolFinite = computed(() => hasPool(props.limits))
const poolRemaining = computed(() => (poolFinite.value && Number.isFinite(props.usage?.remainingSec) ? props.usage.remainingSec : null))
const exhausted = computed(() => poolExhausted(props.limits, props.usage))
const showBetaBadge = computed(() => !poolFinite.value && !planActive.value)
const cycleLeftLabel = computed(() => (poolRemaining.value === null ? '' : `${formatHours(poolRemaining.value)} left this cycle`))

const catalogMaxOutputs = computed(() => props.catalog?.maxOutputs || FALLBACK_CATALOG.maxOutputs)
const planMaxLangs = computed(() => (planActive.value && Number.isInteger(props.limits?.maxLangs) && props.limits.maxLangs > 0 ? props.limits.maxLangs : Infinity))
const maxOutputs = computed(() => Math.min(catalogMaxOutputs.value, planMaxLangs.value))
// The plan, not the service, is what caps the languages.
const planCapsLangs = computed(() => planMaxLangs.value < catalogMaxOutputs.value)
// Validation runs against the effective cap so a flow saved with more
// languages than the plan allows (after a downgrade) is flagged.
const effectiveCatalog = computed(() => (maxOutputs.value === catalogMaxOutputs.value ? props.catalog : { ...props.catalog, maxOutputs: maxOutputs.value }))
const hub = computed(() => props.catalog?.hub || FALLBACK_CATALOG.hub)

function seedFromFlow() {
	// A fresh flow defaults to English spoken and published.
	source.value = props.flow.source || (props.flow.configured ? '' : 'en')
	langs.value = Array.isArray(props.flow.langs) && props.flow.langs.length ? [...props.flow.langs] : (source.value ? [source.value] : [])
	translationDraft.value = draftFromTranslations(props.flow.translations, {
		source: props.flow.source || '',
		langs: props.flow.langs || [],
		catalog: props.catalog,
	})
	serverTranslationErrors.value = {}
	touched.value = { source: false, langs: false }
	attempted.value = false
	clearPruneNotice()
}

function clearPruneNotice() {
	pruneNotice.value = ''
	pruneUndo.value = null
}

seedFromFlow()
watch(
	() => [props.flow.id, props.flow.source, (props.flow.langs || []).join(','), props.flow.configured, translationsKey(props.flow.translations)],
	seedFromFlow,
)

const name = (code) => languageName(code, props.catalog)

const sourceOpts = computed(() => sourceOptions(props.catalog))
const spoken = computed(() => (props.catalog?.languages || []).find((l) => l.code.toLowerCase() === source.value.toLowerCase()) || null)
const sourceIsHub = computed(() => !!source.value && source.value.toLowerCase() === hub.value.toLowerCase())
const sourceTranslates = computed(() => sourceCanTranslate(source.value, props.catalog))
// Core translates any pair (non-English pairs go to Gemini automatically).
const anyPair = computed(() => props.catalog?.nonEnglishPairs === true)
const languagesExplainer = computed(() => (anyPair.value ? LANGUAGES_EXPLAINER_ANY_PAIR : LANGUAGES_EXPLAINER))
const outputOpts = computed(() => (source.value ? outputOptions(source.value, props.catalog) : []))
// Every option the picker could offer is either chosen or disabled: nothing
// left to add (e.g. a non-English source that already publishes itself + English).
const nothingLeftToAdd = computed(() =>
	!!source.value && outputOpts.value.every((o) => o.disabledReason || langs.value.some((l) => l.toLowerCase() === o.code.toLowerCase())),
)

const draft = computed(() => ({ source: source.value, langs: langs.value, delaySec }))
const settingsValidation = computed(() => validateSettings(draft.value, effectiveCatalog.value))

// ------------------------------------------------------------ Translations
const speakersOn = computed(() => props.flow.settings?.speakers === true)
const hasTranslatedLangs = computed(() => translatedLangs(source.value, langs.value).length > 0)
const draftCtx = computed(() => ({ source: source.value, langs: langs.value, catalog: props.catalog }))
const builtTranslations = computed(() => buildTranslations(translationDraft.value, { ...draftCtx.value, speakers: speakersOn.value }))
const savedTranslations = computed(() => {
	const ctx = { source: props.flow.source || '', langs: props.flow.langs || [], catalog: props.catalog }
	return buildTranslations(draftFromTranslations(props.flow.translations, ctx), { ...ctx, speakers: speakersOn.value })
})
// Languages a non-English pair puts on Gemini whatever the switch says.
const requiredGemini = computed(() => requiredGeminiLangs(draftCtx.value))
const translationsChanged = computed(() => translationsKey(builtTranslations.value) !== translationsKey(savedTranslations.value))
// Sent only when there is something to say: Gemini entries, or `{}` to clear
// saved ones. A flow that never used Gemini keeps today's payload.
const includeTranslations = computed(() =>
	Object.keys(builtTranslations.value).length > 0 || Object.keys(props.flow.translations || {}).length > 0)

const translationFieldValue = (lang, field) => (field === 'general' ? null : optionsFor(translationDraft.value, lang)[field])
const translationErrors = computed(() => translationErrorsFor(translationDraft.value, draftCtx.value, serverTranslationErrors.value))
const translationErrorCount = computed(() => Object.values(translationErrors.value).reduce((n, f) => n + Object.keys(f).length, 0))
// Unmapped messages from a `captions_invalid_translations` 400.
const translationGeneralErrors = ref([])

watch(() => props.serverError, (err) => {
	if (err?.code !== 'captions_invalid_translations' || !Array.isArray(err.errors)) {
		serverTranslationErrors.value = {}
		translationGeneralErrors.value = []
		return
	}
	const { mapped, general } = mapServerTranslationErrors(err.errors, translationFieldValue)
	serverTranslationErrors.value = mapped
	translationGeneralErrors.value = general
})

function onGeminiToggle(enabled) {
	track(EVENTS.CAPTIONING_GEMINI_TOGGLED, { flowId: props.flow.id, enabled })
}

const validation = computed(() => ({
	...settingsValidation.value,
	valid: settingsValidation.value.valid && translationErrorCount.value === 0,
}))

const savedSnapshot = computed(() => JSON.stringify({
	source: props.flow.source || '',
	langs: Array.isArray(props.flow.langs) ? props.flow.langs : [],
}))
const draftSnapshot = computed(() => JSON.stringify({
	source: source.value,
	langs: langs.value,
}))
const dirty = computed(() => !props.flow.configured || draftSnapshot.value !== savedSnapshot.value || translationsChanged.value)
// Translation changes go with the same PUT and restart captions like a language change.
const languagesChanged = computed(() =>
	props.flow.configured
	&& (source.value !== (props.flow.source || '') || langs.value.join(',') !== (props.flow.langs || []).join(',') || translationsChanged.value),
)

const canSave = computed(() => dirty.value && validation.value.valid && !props.saving && !props.disabled)

const sourceName = computed(() => name(source.value))
const sourceIncluded = computed(() => !!source.value && langs.value.some((l) => l.toLowerCase() === source.value.toLowerCase()))
const atLimit = computed(() => langs.value.length >= maxOutputs.value)
const planCapHit = computed(() => planCapsLangs.value && atLimit.value)
// Core accepts `langs` without the spoken language; until it says so, the
// spoken track is required.
const sourceOptional = computed(() => props.catalog?.sourceTrackOptional === true)

const chips = computed(() => langs.value.map((code) => ({
	code,
	name: name(code),
	translated: !!source.value && code.toLowerCase() !== source.value.toLowerCase(),
	// Translated by Gemini: automatically (a non-English pair) or by the switch.
	gemini: !!source.value && code.toLowerCase() !== source.value.toLowerCase()
		&& (requiredGemini.value.includes(code) || !!translationDraft.value.gemini),
	geminiWhy: requiredGemini.value.includes(code)
		? `${name(code)} is translated with Gemini: Speechmatics can't translate ${name(source.value)} to ${name(code)}.`
		: `${name(code)} is translated with Gemini.`,
	// A saved output the rules don't allow (e.g. a flow saved before they
	// applied) is flagged so the user sees why Save is blocked.
	blocked: outputDisallowedReason(source.value, code, props.catalog),
	// With the spoken track optional, the last track left can't go too.
	lastTrack: sourceOptional.value && langs.value.length <= 1,
})))
const blockedChips = computed(() => chips.value.filter((c) => c.blocked))

// The spoken track was removed while it is optional: say what viewers lose.
const sourceRemoved = computed(() => sourceOptional.value && !!source.value && !sourceIncluded.value)
const sourceRemovedNote = computed(() => (sourceRemoved.value ? `No ${sourceName.value} captions for viewers.` : ''))
const blockedNotice = computed(() => {
	const list = blockedChips.value
	if (!list.length) return ''
	const names = joinLanguageNames(list.map((c) => c.code), props.catalog)
	const them = list.length === 1 ? 'it' : 'them'
	return `${names} can't be published for a ${sourceName.value} stream. ${list[0].blocked} Remove ${them} to save.`
})

// The panel's own copy covers these, so the field error would repeat it.
const langsError = computed(() => {
	const code = validation.value.errors.langsCode
	if (code === 'not_allowed' || code === 'source_missing') return ''
	return fieldError('langs')
})

// Under the picker only when the rule limits what can be added: the counter
// covers the cap and the chip tags the engine.
const translationHint = computed(() => {
	if (!source.value || !sourceTranslates.value || anyPair.value || sourceIsHub.value) return ''
	return NON_HUB_HINT
})
// Under the source only when it is a constraint.
const sourceConstraint = computed(() => (source.value && !sourceTranslates.value ? `${sourceName.value} can't be translated.` : ''))
const sourceExplainer = computed(() => (anyPair.value ? SOURCE_EXPLAINER_ANY_PAIR : SOURCE_EXPLAINER))

// The saved recognition settings filter profanity, which this source can't do:
// Save/Start turn it off first (CaptioningManage), and this says so up front.
const profanityWillTurnOff = computed(() => {
	const saved = props.flow.settings?.profanity
	return !!source.value && !!saved && saved !== 'off' && !isProfanitySupported(source.value, props.catalog)
})

const addLabel = computed(() => {
	if (!source.value) return 'Choose a source first'
	if (!sourceTranslates.value) return 'No translation'
	if (atLimit.value) return 'Limit reached'
	if (nothingLeftToAdd.value) return 'All added'
	return 'Add language'
})
const addDisabled = computed(() => props.disabled || !source.value || !sourceTranslates.value || atLimit.value || nothingLeftToAdd.value)

function fieldError(field) {
	// A saved source the catalog doesn't offer is shown straight away.
	const savedSourceBad = field === 'source' && props.flow.configured && !touched.value.source
	if (!attempted.value && !touched.value[field] && !savedSourceBad) return ''
	return validation.value.errors[field] || ''
}

// One tracking event per gate per visit, however often it is hovered.
const noted = new Set()
function noteOnce(key, event, payload) {
	if (noted.has(key)) return
	noted.add(key)
	track(event, payload)
}
function noteLanguageBlocked() {
	if (!planCapHit.value) return
	noteOnce('langs', EVENTS.CAPTIONING_LANGUAGE_BLOCKED, { maxLangs: maxOutputs.value, attempted: langs.value.length + 1 })
}
function noteStartBlocked(action) {
	if (!startBlockedCode.value) return
	noteOnce(`start:${startBlockedCode.value}:${action}`, EVENTS.CAPTIONING_CAP_HIT, { code: startBlockedCode.value, action, surface: 'manage' })
}

/**
 * The new spoken language becomes the first track. The other tracks stay as
 * translations where the English-hub rules allow it; the rest are removed
 * with a notice that offers Undo. The previous spoken language is replaced,
 * unless the flow already published translations and it fits under the cap,
 * in which case it stays as one (en + es → de keeps English).
 */
function onSourceChange(next) {
	const previous = source.value
	if (!next || next === previous) return
	const before = { source: previous, langs: [...langs.value] }
	const same = (a, b) => String(a).toLowerCase() === String(b).toLowerCase()
	source.value = next
	touched.value.source = true
	clearPruneNotice()

	const hadTranslations = langs.value.some((l) => !same(l, previous))
	const allowed = (code) => allowedOutputsFor(next, props.catalog).some((c) => same(c, code))
	const candidates = [next, ...langs.value.filter((l) => !same(l, next) && (hadTranslations || !same(l, previous)))]
	let list = candidates.filter(allowed)
	if (list.length > maxOutputs.value) list = list.filter((l) => !same(l, previous))
	langs.value = list

	const removed = candidates.filter((l) => !allowed(l) && !same(l, previous))
	if (!removed.length) return
	const why = outputDisallowedReason(next, removed[0], props.catalog)
	const reason = why === TRANSLATION_RULE ? 'translation is available to or from English.' : why
	pruneNotice.value = `Removed ${joinLanguageNames(removed, props.catalog)}: ${reason}`
	pruneUndo.value = before
}

function undoSourceChange() {
	if (!pruneUndo.value) return
	source.value = pruneUndo.value.source
	langs.value = [...pruneUndo.value.langs]
	clearPruneNotice()
}

function addLang(code) {
	if (!code) return
	if (atLimit.value) {
		noteLanguageBlocked()
		return
	}
	if (langs.value.some((l) => l.toLowerCase() === code.toLowerCase())) return
	if (outputDisallowedReason(source.value, code, props.catalog)) return
	// The spoken language goes first, as a source change puts it.
	if (source.value && code.toLowerCase() === source.value.toLowerCase()) langs.value.unshift(code)
	else langs.value.push(code)
	touched.value.langs = true
	clearPruneNotice()
}

function removeLang(code) {
	if (sourceOptional.value && langs.value.length <= 1) return
	langs.value = langs.value.filter((l) => l !== code)
	touched.value.langs = true
	clearPruneNotice()
}

/** The settings Save/Start send; `translations` only when there is something to say. */
function settingsPayload() {
	const payload = { source: source.value, langs: [...langs.value], delaySec }
	if (includeTranslations.value) payload.translations = { ...builtTranslations.value }
	return payload
}

function submit() {
	attempted.value = true
	if (!canSave.value) return
	emit('save', settingsPayload())
}

const running = computed(() => props.window?.state === 'running')
const ended = computed(() => props.window?.state === 'stopped' || props.window?.state === 'expired')

// ------------------------------------------------------------ Duration presets
// See `durationPresetsFor`: presets above min(maxWindowSec, pool left) go,
// a binding window cap becomes the "(plan max)" top preset, and a finite pool
// swaps "Unlimited" for "Rest of my hours (…)" (still `durationSec: null`).
const durationPresets = computed(() => durationPresetsFor(props.limits, poolRemaining.value))
const restOfHours = computed(() => durationPresets.value.some((p) => p.value === null) && poolFinite.value && planActive.value)

// Keep the selection on an offered preset: the usual 1 hour when it is there,
// else the longest one left, else whatever remains.
watch(durationPresets, (list) => {
	if (list.some((p) => p.value === durationSec.value)) return
	const numeric = list.filter((p) => p.value !== null)
	if (numeric.some((p) => p.value === 3600)) durationSec.value = 3600
	else if (numeric.length) durationSec.value = numeric[numeric.length - 1].value
	else durationSec.value = list.length ? list[0].value : null
}, { immediate: true })

const durationCopy = computed(() => {
	if (!durationPresets.value.length) return 'No captioning hours are left this cycle. Upgrade, or wait for the reset, to start a new window.'
	if (durationSec.value === null) {
		if (restOfHours.value) return `Runs until you stop it or your ${formatHours(poolRemaining.value)} left run out.`
		return 'Runs until you stop it.'
	}
	return 'Counts down from Start, even while the stream is offline.'
})

// A settings change while a window is running is saved with PUT, which
// restarts the runner briefly and keeps the window.
const changeNotice = computed(() => {
	if (!dirty.value || !props.flow.configured || !running.value) return ''
	if (languagesChanged.value) return 'Saving restarts captions briefly.'
	return ''
})

// ------------------------------------------------------------------- Start
// Start sends the whole draft, so it needs a valid form (dirty or not), hours
// left in the pool, and a free concurrency slot.
const concurrencyHit = computed(() => !running.value && concurrencyBlocked(props.limits, props.usage))
const startBlockedCode = computed(() => {
	if (exhausted.value) return 'captions_pool_exhausted'
	if (concurrencyHit.value) return 'captions_concurrency_limit'
	return ''
})
const startBlockedReason = computed(() => {
	if (exhausted.value) return poolExhaustedTooltip(props.limits)
	if (concurrencyHit.value) return concurrencyTooltip(props.limits)
	return ''
})
const canStart = computed(() => validation.value.valid && !props.starting && !props.disabled && !startBlockedReason.value)
// Why Start is greyed out: the plan gate first, then what the form still needs.
const startTooltip = computed(() => {
	if (startBlockedReason.value) return startBlockedReason.value
	if (validation.value.valid || props.disabled) return ''
	return validation.value.errors.langs || validation.value.errors.source || 'Fix the settings above to start.'
})

function start() {
	attempted.value = true
	if (startBlockedReason.value) {
		noteStartBlocked('click')
		return
	}
	if (!canStart.value) return
	// Translation changes are saved first (PUT), see CaptioningManage.
	emit('start', { ...settingsPayload(), translationsChanged: translationsChanged.value, durationSec: durationSec.value })
}

const unreachable = computed(() => props.serverError && [502, 503, 504].includes(props.serverError.status))
const genericError = computed(() => props.serverError && !unreachable.value)
const translationsInvalid = computed(() => props.serverError?.code === 'captions_invalid_translations')
</script>

<template>
	<section
		class="flex flex-col gap-5 rounded-xl bg-surface-2 p-4 sm:p-5"
		:class="flow.configured ? '' : 'ring-1 ring-main/40 shadow-[0_0_0_4px_rgba(62,100,255,0.08)]'"
		aria-labelledby="caption-settings-heading"
	>
		<header class="flex flex-col gap-1">
			<h2 id="caption-settings-heading" class="m-0 text-base font-semibold text-surface-text-2">
				{{ flow.configured ? 'Caption settings' : 'Set up captions' }}
			</h2>
			<p v-if="!flow.configured" class="m-0 text-tiny text-surface-8">Choose the spoken language, the languages to publish, and how long to caption.</p>
		</header>

		<!-- Source language -->
		<div class="flex flex-col gap-2">
			<div class="flex items-center gap-1.5">
				<label for="caption-source" class="m-0 text-tiny font-medium text-surface-text-1">Source language</label>
				<Tooltip :content="sourceExplainer" wrapper-class="inline-flex" popper-class="max-w-[260px] text-sm break-words">
					<button type="button" class="inline-flex text-surface-7 hover:text-white" aria-label="More info: spoken language rules" data-testid="source-explainer">
						<icon-base width="14" height="14" width-view-box="16" height-view-box="16"><icon-info-small /></icon-base>
					</button>
				</Tooltip>
			</div>
			<LanguageSelect
				id="caption-source"
				:model-value="source"
				:options="sourceOpts"
				placeholder="What language is spoken on the stream?"
				aria-label="Source language"
				:disabled="disabled"
				@update:model-value="onSourceChange"
			/>
			<p v-if="fieldError('source')" class="m-0 text-xs text-error-light" role="alert">{{ fieldError('source') }}</p>
			<p v-else-if="sourceConstraint" class="m-0 text-xs text-gold" data-testid="source-constraint">{{ sourceConstraint }}</p>
		</div>

		<!-- Caption languages -->
		<div class="flex flex-col gap-2">
			<div class="flex items-center justify-between gap-2">
				<span class="flex items-center gap-1.5">
					<span id="caption-langs-label" class="text-tiny font-medium text-surface-text-1">Caption languages</span>
					<Tooltip :content="languagesExplainer" wrapper-class="inline-flex" popper-class="max-w-[260px] text-sm break-words">
						<button type="button" class="inline-flex text-surface-7 hover:text-white" aria-label="More info: how languages are produced" data-testid="langs-explainer">
							<icon-base width="14" height="14" width-view-box="16" height-view-box="16"><icon-info-small /></icon-base>
						</button>
					</Tooltip>
				</span>
				<span class="text-[11px] tabular-nums text-surface-7">
					<Tooltip
						:active="planCapHit"
						:content="`Your plan includes ${maxOutputs} ${maxOutputs === 1 ? 'language' : 'languages'} per flow`"
						wrapper-class="inline-flex"
					>
						<span data-testid="langs-count">{{ langs.length }} of {{ maxOutputs }}</span>
					</Tooltip>
					<template v-if="planCapHit">
						·
						<router-link :to="UPGRADE_ROUTE" class="font-medium text-main-light hover:underline" data-testid="lang-cap-hint" @click="track(EVENTS.UPGRADE_CLICKED, { source: 'captioning-languages' })">Upgrade</router-link>
					</template>
				</span>
			</div>
			<ul class="m-0 flex list-none flex-wrap items-center gap-2 p-0" aria-labelledby="caption-langs-label" data-testid="caption-langs">
				<li
					v-for="chip in chips"
					:key="chip.code"
					class="inline-flex h-8 items-center gap-1.5 rounded-full pl-3 pr-1 text-tiny font-medium ring-1"
					:class="chip.blocked ? 'bg-error-transparent text-error-light ring-error/30' : 'bg-surface-3 text-surface-text-2 ring-white/5'"
					:title="chip.blocked || ''"
				>
					<span>{{ chip.name }}</span>
					<span v-if="chip.blocked" class="text-[10px] uppercase tracking-wide">not allowed</span>
					<template v-else-if="chip.translated">
						<span class="text-[10px] uppercase tracking-wide text-surface-7">translated</span>
						<Tooltip v-if="chip.gemini" :content="chip.geminiWhy" wrapper-class="inline-flex" popper-class="max-w-[260px] text-sm break-words">
							<span class="rounded bg-main-transparent px-1 text-[10px] font-semibold text-main-light" tabindex="0" data-testid="gemini-tag">Gemini</span>
						</Tooltip>
					</template>
					<span v-else class="text-[10px] uppercase tracking-wide text-main-light">spoken</span>
					<Tooltip :active="chip.lastTrack" content="Add another language first." wrapper-class="flex" wrap-trigger>
						<button
							type="button"
							class="ml-0.5 flex size-6 items-center justify-center rounded-full text-surface-7 transition-colors hover:bg-surface-4 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
							:aria-label="`Remove ${chip.name}`"
							:disabled="disabled || chip.lastTrack"
							@click="removeLang(chip.code)"
						>
							<icon-base width="10" height="10"><icon-cross /></icon-base>
						</button>
					</Tooltip>
				</li>
				<li>
					<!-- The wrapper hears the hover/click a disabled trigger swallows, for the blocked-language event. -->
					<span class="inline-flex" data-testid="add-language" @mouseenter="noteLanguageBlocked" @click="noteLanguageBlocked">
						<LanguageSelect
							variant="chip"
							:model-value="''"
							:options="outputOpts"
							:disabled-codes="langs"
							:disabled="addDisabled"
							:trigger-label="addLabel"
							aria-label="Add caption language"
							@select="addLang"
						>
							<template #trigger-icon>
								<icon-base width="10" height="10" width-view-box="12" height-view-box="12"><icon-plus-tiny /></icon-base>
							</template>
						</LanguageSelect>
					</span>
				</li>
			</ul>
			<p v-if="langsError" class="m-0 text-xs text-error-light" role="alert">{{ langsError }}</p>
			<p v-if="blockedNotice" class="m-0 text-xs text-error-light" role="alert" data-testid="invalid-langs">{{ blockedNotice }}</p>
			<p v-if="pruneNotice" class="m-0 text-xs text-gold" role="status" data-testid="prune-notice">
				<span data-testid="prune-notice-text">{{ pruneNotice }}</span>
				<button type="button" class="ml-1 font-medium text-main-light hover:underline" :disabled="disabled" @click="undoSourceChange">Undo</button>
			</p>
			<p v-if="sourceRemovedNote" class="m-0 text-xs text-surface-7" data-testid="source-removed">
				<span data-testid="source-removed-text">{{ sourceRemovedNote }}</span>
				<template v-if="!atLimit">
					·
					<button type="button" class="font-medium text-main-light hover:underline" :disabled="disabled" @click="addLang(source)">Add {{ sourceName }} back</button>
				</template>
				<template v-else> Remove a language to add {{ sourceName }} back.</template>
			</p>
			<p v-if="source && !sourceOptional && !sourceIncluded && !atLimit" class="m-0 text-xs text-gold" data-testid="source-missing">
				Spoken language required ·
				<button type="button" class="font-medium text-main-light hover:underline" @click="addLang(source)">Add {{ sourceName }}</button>
			</p>
			<p v-else-if="source && !sourceOptional && !sourceIncluded && atLimit" class="m-0 text-xs text-gold" data-testid="source-missing">
				Spoken language required · remove a language to make room for {{ sourceName }}.
			</p>
			<p v-else-if="translationHint && !langsError && !blockedNotice && !sourceRemoved" class="m-0 text-xs text-surface-7" data-testid="translation-hint">
				{{ translationHint }}
			</p>
			<p v-if="profanityWillTurnOff" class="m-0 text-xs text-gold" data-testid="profanity-turns-off">
				Profanity filter isn't available for {{ sourceName }}; it turns off on {{ running ? 'save' : 'start' }}.
			</p>
		</div>

		<!-- Translation -->
		<TranslationEngine
			v-if="hasTranslatedLangs"
			v-model="translationDraft"
			:source="source"
			:langs="langs"
			:catalog="catalog"
			:speakers="speakersOn"
			:required="requiredGemini"
			:errors="translationErrors"
			:server-errors="serverTranslationErrors"
			:disabled="disabled"
			@toggle="onGeminiToggle"
		/>

		<!-- Duration (next window); a running window's controls are in the session card. -->
		<div v-if="!running" class="flex flex-col gap-2" data-testid="caption-duration">
			<div class="flex items-baseline justify-between gap-2">
				<span id="caption-duration-label" class="text-tiny font-medium text-surface-text-1">Duration</span>
				<span v-if="cycleLeftLabel" class="text-[11px] tabular-nums text-surface-7" data-testid="cycle-left">{{ cycleLeftLabel }}</span>
				<span v-else-if="showBetaBadge" class="rounded-full bg-main-transparent px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-main-light">Free during beta</span>
			</div>
			<!-- No pills when the pool is used up: the copy below says why and Start is gated. -->
			<div v-if="durationPresets.length" class="flex flex-wrap gap-1.5" role="radiogroup" aria-labelledby="caption-duration-label">
				<button
					v-for="preset in durationPresets"
					:key="String(preset.value)"
					type="button"
					role="radio"
					:aria-checked="durationSec === preset.value"
					:aria-label="preset.label"
					:disabled="disabled"
					class="inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-tiny font-medium ring-1 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
					:class="durationSec === preset.value
						? 'bg-main text-white ring-main'
						: 'bg-surface-3 text-surface-text-1 ring-white/5 hover:bg-surface-4 hover:text-white'"
					:data-duration="String(preset.value)"
					@click="durationSec = preset.value"
				>
					{{ preset.label }}
				</button>
			</div>
			<p class="m-0 text-xs text-surface-7" data-testid="duration-copy">{{ durationCopy }}</p>
		</div>

		<!-- Feedback -->
		<Alert v-if="unreachable" type="errwarning" role="alert">
			<div class="flex w-full flex-wrap items-center gap-2">
				<span class="flex-1">The captioning service isn't reachable right now. Try again.</span>
				<FormButton type="secondary" size="sm" class="!text-white !ring-white/40" :is-loading="saving" @click="emit('retry')">Retry</FormButton>
			</div>
		</Alert>
		<Alert v-else-if="translationsInvalid" type="error" role="alert" data-error-code="captions_invalid_translations">
			<span class="flex flex-col gap-1">
				<span>Some translation settings need fixing before this can be saved.</span>
				<span v-for="(msg, i) in translationGeneralErrors" :key="i" class="text-xs">{{ msg }}</span>
			</span>
		</Alert>
		<Alert v-else-if="genericError" type="error" role="alert" :data-error-code="serverError.code || undefined">
			<span class="flex w-full flex-wrap items-center gap-x-1 gap-y-1">
				<span>{{ serverError.message }}</span>
				<router-link v-if="serverError.upgrade" :to="UPGRADE_ROUTE" class="font-medium text-white underline" @click="track(EVENTS.UPGRADE_CLICKED, { source: 'captioning-limit' })">
					Upgrade
				</router-link>
			</span>
		</Alert>

		<footer class="mt-auto flex flex-col gap-3 border-t border-surface-3 pt-4">
			<div class="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
				<template v-if="running">
					<span v-if="changeNotice && !serverError" class="text-xs text-surface-7 sm:mr-auto" role="note" data-testid="restart-notice">{{ changeNotice }}</span>
					<FormButton v-if="dirty" type="secondary" size="md" class="justify-center" :disabled="saving" @click="seedFromFlow">
						Reset
					</FormButton>
					<FormButton type="main" size="md" class="justify-center" :disabled="!canSave" :is-loading="saving" @click="submit">
						Save changes
					</FormButton>
				</template>
				<template v-else>
					<FormButton v-if="dirty && flow.configured" type="secondary" size="md" class="justify-center" :disabled="starting" @click="seedFromFlow">
						Reset
					</FormButton>
					<!-- The wrapper hears the hover a disabled button swallows, for the cap-hit event. -->
					<span
						class="flex sm:inline-flex"
						data-testid="start-wrapper"
						:data-blocked="startBlockedCode || undefined"
						@mouseenter="noteStartBlocked('hover')"
						@click="startBlockedCode && noteStartBlocked('click')"
					>
						<Tooltip :active="!!startTooltip" :content="startTooltip" wrap-trigger wrapper-class="w-full" :hide="800">
							<FormButton type="main" size="md" class="w-full justify-center" :disabled="!canStart" :is-loading="starting" @click="start">
								{{ ended ? 'Start again' : 'Start captions' }}
							</FormButton>
						</Tooltip>
					</span>
				</template>
			</div>
		</footer>
	</section>
</template>
